import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { FileText, Plus, Check, Lock, Download, Edit2, Save, X, AlertCircle, BookOpen, Loader2, Settings2, Mail, Trash2 } from 'lucide-react';
import { BudgetEmailScheduler } from '@/components/BudgetEmailScheduler';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status labels
const YEAR_STATUS = {
  PLANNING: { label: 'In Pianificazione', color: 'bg-yellow-100 text-yellow-800' },
  ACTIVE: { label: 'Attivo', color: 'bg-green-100 text-green-800' },
  CLOSED: { label: 'Chiuso', color: 'bg-gray-100 text-gray-800' },
  ARCHIVED: { label: 'Archiviato', color: 'bg-slate-100 text-slate-800' }
};

// Category labels
const CATEGORY_LABELS = {
  ENERGIA: 'Energia elettrica',
  ACQUA: 'Acqua',
  GAS: 'Gas',
  RISCALDAMENTO: 'Riscaldamento',
  MANUTENZIONE_ORDINARIA: 'Manutenzione ordinaria',
  MANUTENZIONE_STRAORDINARIA: 'Manutenzione straordinaria',
  PULIZIA: 'Pulizia',
  ASSICURAZIONE: 'Assicurazione',
  ASCENSORE: 'Ascensore',
  PORTIERE: 'Portiere',
  GIARDINIERE: 'Giardiniere',
  AMMINISTRAZIONE: 'Amministrazione',
  COMPENSO_AMMINISTRATORE: 'Compenso amministratore',
  SPESE_BANCARIE: 'Spese bancarie',
  SPESE_LEGALI: 'Spese legali',
  FONDO_CASSA: 'Fondo cassa',
  FONDO_LAVORI: 'Fondo lavori',
  ALTRO: 'Altro'
};

export const Budgets = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  // Data state
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isNewYearOpen, setIsNewYearOpen] = useState(false);
  
  // Edit state for budget lines
  const [editingLineId, setEditingLineId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  
  // Relazione state
  const [isRelazioneOpen, setIsRelazioneOpen] = useState(false);
  const [relazioneText, setRelazioneText] = useState('');
  const [relazioneLoading, setRelazioneLoading] = useState(false);
  const [relazioneSaving, setRelazioneSaving] = useState(false);
  
  // Custom docs selection state
  const [isCustomDocsOpen, setIsCustomDocsOpen] = useState(false);
  const [customDocsType, setCustomDocsType] = useState('PREVENTIVO');
  const [availableDocs, setAvailableDocs] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [customDocsLoading, setCustomDocsLoading] = useState(false);
  const [customDocsGenerating, setCustomDocsGenerating] = useState(false);
  
  // Preset management state
  const [presets, setPresets] = useState({ system_presets: [], custom_presets: [] });
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDesc, setNewPresetDesc] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  
  // Email scheduler state
  const [isEmailSchedulerOpen, setIsEmailSchedulerOpen] = useState(false);
  
  // Forms
  // Note dell'esercizio: rilievi da portarsi dietro fino all'approvazione
  const [notesText, setNotesText] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);

  const [yearForm, setYearForm] = useState({
    year: new Date().getFullYear() + 1,
    start_date: '',
    end_date: '',
    description: ''
  });

  // Fetch functions
  const fetchYears = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/financial-years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setYears(data);
        if (data.length > 0 && !selectedYear) {
          setSelectedYear(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching years:', error);
    }
  }, [condoId, token, selectedYear]);

  const fetchBudgetSummary = useCallback(async (yearId) => {
    if (!condoId || !yearId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/financial-years/${yearId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBudgetSummary(data);
      }
    } catch (error) {
      console.error('Error fetching budget:', error);
    }
  }, [condoId, token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchYears();
      setLoading(false);
    };
    loadData();
  }, [fetchYears]);

  useEffect(() => {
    if (selectedYear?.id) {
      fetchBudgetSummary(selectedYear.id);
    }
  }, [selectedYear?.id, fetchBudgetSummary]);

  // Create financial year
  const handleCreateYear = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/financial-years`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(yearForm)
      });
      if (res.ok) {
        toast.success('Esercizio creato');
        setIsNewYearOpen(false);
        setYearForm({
          year: new Date().getFullYear() + 1,
          start_date: '',
          end_date: '',
          description: ''
        });
        fetchYears();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore creazione');
      }
    } catch (error) {
      toast.error('Errore creazione');
    }
  };

  // Activate year
  const handleActivateYear = async () => {
    if (!selectedYear) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/activate`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('Esercizio attivato');
        fetchYears();
        fetchBudgetSummary(selectedYear.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Le note seguono l'esercizio selezionato, ma non si sovrascrive quello che
  // l'utente sta scrivendo: si ricarica solo quando non ci sono modifiche aperte.
  useEffect(() => {
    if (!notesDirty) {
      setNotesText(selectedYear?.notes || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id, selectedYear?.notes]);

  const handleSaveNotes = async () => {
    if (!selectedYear) return;
    setNotesSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/notes`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ notes: notesText })
        }
      );
      if (res.ok) {
        toast.success(`Note dell'esercizio ${selectedYear.year} salvate`);
        setNotesDirty(false);
        fetchYears();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio note');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setNotesSaving(false);
    }
  };

  // Close year
  const handleCloseYear = async () => {
    if (!selectedYear) return;
    if (!window.confirm('Chiudere definitivamente questo esercizio?')) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/close`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('Esercizio chiuso');
        fetchYears();
        fetchBudgetSummary(selectedYear.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Update budget line
  // Creando un esercizio l'app genera una voce vuota per ogni categoria: quelle
  // che non servono restavano nel preventivo senza modo di toglierle.
  const handleDeleteLine = async (line) => {
    const valorizzata =
      (line.planned_amount || 0) !== 0 ||
      (line.actual_amount || 0) !== 0 ||
      !!(line.description || '').trim();
    const messaggio = valorizzata
      ? 'Questa voce contiene importi o una descrizione. Eliminarla comunque?'
      : 'Eliminare questa voce di bilancio?';
    if (!window.confirm(messaggio)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/lines/${line.id}` +
          (valorizzata ? '?force=true' : ''),
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Errore durante l'eliminazione");
      }
      toast.success('Voce di bilancio eliminata');
      await fetchBudgetSummary(selectedYear.id);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleUpdateLine = async (lineId) => {
    if (!selectedYear) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/lines/${lineId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ planned_amount: parseFloat(editingValue) || 0 })
        }
      );
      if (res.ok) {
        toast.success('Importo aggiornato');
        setEditingLineId(null);
        fetchBudgetSummary(selectedYear.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Generate PDF
  const handleGeneratePDF = async (type) => {
    if (!selectedYear) return;
    try {
      const endpoint = type === 'preventivo' ? 'generate-preventivo' : 'generate-consuntivo';
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/${endpoint}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore generazione PDF');
      }
    } catch (error) {
      toast.error('Errore generazione PDF');
    }
  };

  // Download budget PDF with meeting minutes attached (for residents)
  const handleDownloadWithMinutes = async (type) => {
    if (!selectedYear) return;
    try {
      const budgetType = type === 'preventivo' ? 'PREVENTIVO' : 'CONSUNTIVO';
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/download-with-minutes?budget_type=${budgetType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const minutesAttached = res.headers.get('X-Minutes-Attached');
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bilancio_${type}_${selectedYear.year}_con_verbale.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        if (minutesAttached === 'false') {
          toast.warning('Bilancio scaricato, ma non è stato trovato un verbale di assemblea da allegare.');
        } else {
          toast.success('Download completato (bilancio con verbale allegato)');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore download PDF');
      }
    } catch (error) {
      toast.error('Errore download PDF');
    }
  };

  // Generate Relazione
  const handleGenerateRelazione = async () => {
    if (!selectedYear) return;
    setRelazioneLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/generate-relazione`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRelazioneText(data.text);
        setIsRelazioneOpen(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore generazione relazione');
      }
    } catch (error) {
      toast.error('Errore generazione relazione');
    } finally {
      setRelazioneLoading(false);
    }
  };

  // Download complete budget documentation (normativa-compliant)
  const handleDownloadBudgetDocs = async (docType) => {
    if (!selectedYear) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/budget-documentation?doc_type=${docType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const typeLabel = docType === 'PREVENTIVO' ? 'preventivo' : 'consuntivo';
        a.download = `bilancio_${typeLabel}_${selectedYear.year}_documentazione_completa.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Documentazione bilancio scaricata');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore download documentazione');
      }
    } catch (error) {
      toast.error('Errore download documentazione');
    }
  };

  // Save Relazione as PDF
  const handleSaveRelazione = async () => {
    if (!selectedYear || !relazioneText) return;
    setRelazioneSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/save-relazione`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ text: relazioneText })
        }
      );
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        toast.info('La relazione è visibile a tutti i condomini nella sezione Documenti');
        setIsRelazioneOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio relazione');
      }
    } catch (error) {
      toast.error('Errore salvataggio relazione');
    } finally {
      setRelazioneSaving(false);
    }
  };

  // Open custom docs dialog and fetch available documents
  const handleOpenCustomDocs = async (docType) => {
    if (!selectedYear) return;
    setCustomDocsType(docType);
    setCustomDocsLoading(true);
    setIsCustomDocsOpen(true);
    setSelectedPreset(null);
    setShowSavePreset(false);
    setNewPresetName('');
    setNewPresetDesc('');
    
    try {
      // Fetch both documents config and presets in parallel
      const [docsRes, presetsRes] = await Promise.all([
        fetch(
          `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/budget-documents-config?doc_type=${docType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        fetch(
          `${API_URL}/api/condominiums/${condoId}/document-presets?doc_type=${docType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      
      if (docsRes.ok) {
        const data = await docsRes.json();
        setAvailableDocs(data.documents);
        // Pre-select required documents
        const requiredDocs = data.documents.filter(d => d.required).map(d => d.id);
        setSelectedDocs(requiredDocs);
      } else {
        toast.error('Errore caricamento documenti disponibili');
        setIsCustomDocsOpen(false);
        return;
      }
      
      if (presetsRes.ok) {
        const presetsData = await presetsRes.json();
        setPresets(presetsData);
      }
    } catch (error) {
      toast.error('Errore caricamento documenti disponibili');
      setIsCustomDocsOpen(false);
    } finally {
      setCustomDocsLoading(false);
    }
  };

  // Apply a preset
  const handleApplyPreset = (preset) => {
    setSelectedPreset(preset);
    setSelectedDocs(preset.selected_docs);
    toast.success(`Preset "${preset.name}" applicato`);
  };

  // Save current selection as a new preset
  const handleSavePreset = async () => {
    if (!newPresetName.trim()) {
      toast.error('Inserisci un nome per il preset');
      return;
    }
    
    setSavingPreset(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/document-presets`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({
            name: newPresetName.trim(),
            doc_type: customDocsType,
            selected_docs: selectedDocs,
            description: newPresetDesc.trim() || null
          })
        }
      );
      
      if (res.ok) {
        const newPreset = await res.json();
        setPresets(prev => ({
          ...prev,
          custom_presets: [...prev.custom_presets, newPreset]
        }));
        toast.success(`Preset "${newPresetName}" salvato`);
        setShowSavePreset(false);
        setNewPresetName('');
        setNewPresetDesc('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio preset');
      }
    } catch (error) {
      toast.error('Errore salvataggio preset');
    } finally {
      setSavingPreset(false);
    }
  };

  // Delete a custom preset
  const handleDeletePreset = async (presetId, presetName) => {
    if (!confirm(`Eliminare il preset "${presetName}"?`)) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/document-presets/${presetId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        setPresets(prev => ({
          ...prev,
          custom_presets: prev.custom_presets.filter(p => p.id !== presetId)
        }));
        toast.success('Preset eliminato');
        if (selectedPreset?.id === presetId) {
          setSelectedPreset(null);
        }
      } else {
        toast.error('Errore eliminazione preset');
      }
    } catch (error) {
      toast.error('Errore eliminazione preset');
    }
  };

  // Toggle document selection
  const toggleDocSelection = (docId, isRequired) => {
    if (isRequired) return; // Can't deselect required documents
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Select/Deselect all optional documents
  const handleSelectAll = () => {
    const allDocIds = availableDocs.map(d => d.id);
    setSelectedDocs(allDocIds);
  };

  const handleSelectRequired = () => {
    const requiredDocIds = availableDocs.filter(d => d.required).map(d => d.id);
    setSelectedDocs(requiredDocIds);
  };

  // Generate custom documentation
  const handleGenerateCustomDocs = async () => {
    if (!selectedYear || selectedDocs.length === 0) return;
    setCustomDocsGenerating(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/financial-years/${selectedYear.id}/budget-documentation-custom`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({
            doc_type: customDocsType,
            selected_docs: selectedDocs
          })
        }
      );
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const typeLabel = customDocsType === 'PREVENTIVO' ? 'preventivo' : 'consuntivo';
        a.download = `bilancio_${typeLabel}_${selectedYear.year}_personalizzato.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Documentazione personalizzata generata (${selectedDocs.length} documenti)`);
        setIsCustomDocsOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore generazione documentazione');
      }
    } catch (error) {
      toast.error('Errore generazione documentazione');
    } finally {
      setCustomDocsGenerating(false);
    }
  };

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const canEdit = userRole === 'ADMIN' || userRole === 'ACCOUNTANT';
  const canAdmin = userRole === 'ADMIN';
  const canEditLines = canEdit && selectedYear?.status !== 'CLOSED' && selectedYear?.status !== 'ARCHIVED';

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Bilanci"
        subtitle="Preventivi e consuntivi"
        actions={canAdmin ? (
          <Dialog open={isNewYearOpen} onOpenChange={setIsNewYearOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-primary hover:bg-white/90">
                <Plus className="mr-2 h-4 w-4" />
                Nuovo Esercizio
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuovo Esercizio Finanziario</DialogTitle>
                <DialogDescription>Crea un nuovo anno di bilancio</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateYear} className="space-y-4">
                <div className="space-y-2">
                  <Label>Anno *</Label>
                  <Input
                    type="number"
                    value={yearForm.year}
                    onChange={(e) => setYearForm({ ...yearForm, year: parseInt(e.target.value) })}
                    min={2020}
                    max={2100}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Inizio *</Label>
                    <Input
                      type="date"
                      value={yearForm.start_date}
                      onChange={(e) => setYearForm({ ...yearForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Fine *</Label>
                    <Input
                      type="date"
                      value={yearForm.end_date}
                      onChange={(e) => setYearForm({ ...yearForm, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Input
                    value={yearForm.description}
                    onChange={(e) => setYearForm({ ...yearForm, description: e.target.value })}
                    placeholder="Es: Esercizio ordinario 2025"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit">Crea Esercizio</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        ) : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Years List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Esercizi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <p className="text-muted-foreground text-sm">Caricamento...</p>
            ) : years.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nessun esercizio creato</p>
            ) : (
              years.map((year) => {
                const statusInfo = YEAR_STATUS[year.status] || YEAR_STATUS.PLANNING;
                return (
                  <div
                    key={year.id}
                    onClick={() => setSelectedYear(year)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedYear?.id === year.id
                        ? 'border-slate-900 bg-slate-50'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{year.year}</p>
                        <p className="text-xs text-muted-foreground">
                          {year.start_date} - {year.end_date}
                        </p>
                      </div>
                      <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Budget Detail */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  {selectedYear ? `Bilancio ${selectedYear.year}` : 'Seleziona un esercizio'}
                </CardTitle>
                {selectedYear && (
                  <CardDescription>
                    {selectedYear.start_date} - {selectedYear.end_date}
                  </CardDescription>
                )}
              </div>
              {selectedYear && canAdmin && (
                <div className="flex gap-2 flex-wrap">
                  {selectedYear.status === 'PLANNING' && (
                    <Button size="sm" variant="outline" onClick={handleActivateYear}>
                      <Check className="h-4 w-4 mr-1" />
                      Attiva
                    </Button>
                  )}
                  {selectedYear.status === 'ACTIVE' && (
                    <Button size="sm" variant="outline" onClick={handleCloseYear}>
                      <Lock className="h-4 w-4 mr-1" />
                      Chiudi
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleGeneratePDF('preventivo')}>
                    <Download className="h-4 w-4 mr-1" />
                    Preventivo PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleGeneratePDF('consuntivo')}>
                    <Download className="h-4 w-4 mr-1" />
                    Consuntivo PDF
                  </Button>
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={handleGenerateRelazione}
                    disabled={relazioneLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {relazioneLoading ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <BookOpen className="h-4 w-4 mr-1" />
                    )}
                    Genera Relazione
                  </Button>
                  {/* Documentazione completa secondo normativa */}
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={() => handleDownloadBudgetDocs('PREVENTIVO')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    title="Documentazione completa art. 1135 c.c.: convocazione, relazione, preventivo capitoli, riparto, piano rate, millesimi, delibera fondi"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Doc. Preventivo Completa
                  </Button>
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={() => handleDownloadBudgetDocs('CONSUNTIVO')}
                    className="bg-purple-600 hover:bg-purple-700"
                    title="Documentazione completa art. 1130-bis c.c.: convocazione, registro contabilità, riepilogo finanziario, nota sintetica, riparto, confronto, situazione patrimoniale, estratti conto, documentazione giustificativa, morosità"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Doc. Consuntivo Completa
                  </Button>
                  {/* Documentazione personalizzata */}
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleOpenCustomDocs('PREVENTIVO')}
                    title="Seleziona i documenti da includere nel preventivo"
                  >
                    <Settings2 className="h-4 w-4 mr-1" />
                    Preventivo Personalizzato
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleOpenCustomDocs('CONSUNTIVO')}
                    title="Seleziona i documenti da includere nel consuntivo"
                  >
                    <Settings2 className="h-4 w-4 mr-1" />
                    Consuntivo Personalizzato
                  </Button>
                </div>
              )}
              {/* Download buttons for all users (including residents) */}
              {selectedYear && !canAdmin && (
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleDownloadWithMinutes('consuntivo')}
                    title="Scarica il bilancio consuntivo con il verbale dell'assemblea di approvazione"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Consuntivo + Verbale
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleDownloadWithMinutes('preventivo')}
                    title="Scarica il bilancio preventivo con il verbale dell'assemblea di approvazione"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Preventivo + Verbale
                  </Button>
                  {/* Documentazione completa per residenti */}
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={() => handleDownloadBudgetDocs('PREVENTIVO')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    title="Documentazione preventivo completa (7 documenti)"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Doc. Preventivo Completa
                  </Button>
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={() => handleDownloadBudgetDocs('CONSUNTIVO')}
                    className="bg-purple-600 hover:bg-purple-700"
                    title="Documentazione consuntivo completa (10 documenti)"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Doc. Consuntivo Completa
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedYear ? (
              <p className="text-center text-muted-foreground py-8">
                Seleziona un esercizio per visualizzare il bilancio
              </p>
            ) : !budgetSummary ? (
              <p className="text-center text-muted-foreground py-8">Caricamento...</p>
            ) : (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Totale Preventivo</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(budgetSummary.total_planned)}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Totale Consuntivo</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(budgetSummary.total_actual)}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${
                    budgetSummary.total_variance >= 0 ? 'bg-red-50' : 'bg-green-50'
                  }`}>
                    <p className="text-sm text-muted-foreground">Variazione</p>
                    <p className={`text-2xl font-bold ${
                      budgetSummary.total_variance >= 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {budgetSummary.total_variance >= 0 ? '+' : ''}{formatCurrency(budgetSummary.total_variance)}
                    </p>
                  </div>
                </div>

                {/* Budget Lines Table */}
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="text-right">Preventivo €</TableHead>
                        <TableHead className="text-right">Consuntivo €</TableHead>
                        <TableHead className="text-right">Variazione €</TableHead>
                        {canEditLines && <TableHead className="w-20"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {budgetSummary.lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell className="font-medium">
                            {CATEGORY_LABELS[line.category] || line.category}
                          </TableCell>
                          <TableCell className="text-right">
                            {editingLineId === line.id ? (
                              <Input
                                type="number"
                                step="0.01"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                className="w-28 text-right"
                                autoFocus
                              />
                            ) : (
                              formatCurrency(line.planned_amount)
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(line.actual_amount)}
                          </TableCell>
                          <TableCell className={`text-right ${
                            line.variance > 0 ? 'text-red-600' : line.variance < 0 ? 'text-green-600' : ''
                          }`}>
                            {line.variance > 0 ? '+' : ''}{formatCurrency(line.variance)}
                          </TableCell>
                          {canEditLines && (
                            <TableCell>
                              {editingLineId === line.id ? (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => handleUpdateLine(line.id)}
                                  >
                                    <Save className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => setEditingLineId(null)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingLineId(line.id);
                                      setEditingValue(line.planned_amount.toString());
                                    }}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-red-700"
                                    title="Elimina voce"
                                    onClick={() => handleDeleteLine(line)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-slate-50 font-bold">
                        <TableCell>TOTALE</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(budgetSummary.total_planned)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(budgetSummary.total_actual)}
                        </TableCell>
                        <TableCell className={`text-right ${
                          budgetSummary.total_variance > 0 ? 'text-red-600' : budgetSummary.total_variance < 0 ? 'text-green-600' : ''
                        }`}>
                          {budgetSummary.total_variance > 0 ? '+' : ''}{formatCurrency(budgetSummary.total_variance)}
                        </TableCell>
                        {canEditLines && <TableCell />}
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Note dell'esercizio */}
      {selectedYear && (
        <Card className="mt-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  Note dell'esercizio {selectedYear.year}
                </CardTitle>
                <CardDescription>
                  Rilievi da portarsi dietro fino all'approvazione: criticità sulle
                  tabelle, conguagli da chiedere, rimborsi dovuti. Restano anche a
                  esercizio chiuso.
                </CardDescription>
              </div>
              {selectedYear.notes_updated_at && !notesDirty && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  aggiornate il{' '}
                  {new Date(selectedYear.notes_updated_at).toLocaleDateString('it-IT')}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {canEdit ? (
              <>
                <textarea
                  className="w-full min-h-[160px] rounded-md border border-input bg-background
                             px-3 py-2 text-sm font-mono leading-relaxed
                             focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Nessuna nota su questo esercizio."
                  value={notesText}
                  onChange={(e) => {
                    setNotesText(e.target.value);
                    setNotesDirty(true);
                  }}
                />
                <div className="flex items-center justify-end gap-2 mt-3">
                  {notesDirty && (
                    <span className="text-xs text-amber-700 mr-auto">
                      modifiche non salvate
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!notesDirty || notesSaving}
                    onClick={() => {
                      setNotesText(selectedYear.notes || '');
                      setNotesDirty(false);
                    }}
                  >
                    Annulla
                  </Button>
                  <Button size="sm" disabled={!notesDirty || notesSaving} onClick={handleSaveNotes}>
                    {notesSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    Salva note
                  </Button>
                </div>
              </>
            ) : selectedYear.notes ? (
              <p className="text-sm whitespace-pre-wrap font-mono leading-relaxed">
                {selectedYear.notes}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nessuna nota su questo esercizio.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Relazione Dialog */}
      <Dialog open={isRelazioneOpen} onOpenChange={setIsRelazioneOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              Relazione al Bilancio {selectedYear?.year}
            </DialogTitle>
            <DialogDescription>
              Modifica il testo della relazione prima di generare il PDF. Il documento sarà visibile a tutti i condomini.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                <strong>Nota sulla privacy:</strong> I dati sulle morosità sono aggregati e non includono nomi di unità o proprietari.
              </p>
            </div>
            
            <Textarea
              value={relazioneText}
              onChange={(e) => setRelazioneText(e.target.value)}
              className="h-[400px] font-mono text-sm resize-none"
              placeholder="Testo della relazione..."
            />
          </div>
          
          <DialogFooter className="flex gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsRelazioneOpen(false)}>
              Annulla
            </Button>
            <Button variant="outline" onClick={handleGenerateRelazione} disabled={relazioneLoading}>
              {relazioneLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Rigenera
            </Button>
            <Button 
              onClick={handleSaveRelazione} 
              disabled={relazioneSaving || !relazioneText}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {relazioneSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salva PDF e Archivia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Documents Selection Dialog */}
      <Dialog open={isCustomDocsOpen} onOpenChange={setIsCustomDocsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {customDocsType === 'PREVENTIVO' ? 'Documentazione Preventivo' : 'Documentazione Consuntivo'}
            </DialogTitle>
            <DialogDescription>
              Seleziona i documenti da includere o usa un preset predefinito.
            </DialogDescription>
          </DialogHeader>
          
          {customDocsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
              {/* Presets section */}
              {(presets.system_presets.length > 0 || presets.custom_presets.length > 0) && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Preset Rapidi</Label>
                  <div className="flex flex-wrap gap-2">
                    {presets.system_presets.map((preset) => (
                      <Button
                        key={preset.id}
                        size="sm"
                        variant={selectedPreset?.id === preset.id ? "default" : "outline"}
                        onClick={() => handleApplyPreset(preset)}
                        title={preset.description}
                        className="text-xs"
                        data-testid={`preset-${preset.id}`}
                      >
                        {preset.name}
                      </Button>
                    ))}
                    {presets.custom_presets.map((preset) => (
                      <div key={preset.id} className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant={selectedPreset?.id === preset.id ? "default" : "secondary"}
                          onClick={() => handleApplyPreset(preset)}
                          title={preset.description || 'Preset personalizzato'}
                          className="text-xs"
                          data-testid={`preset-${preset.id}`}
                        >
                          ⭐ {preset.name}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePreset(preset.id, preset.name)}
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Elimina preset"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Quick actions */}
              <div className="flex gap-2 items-center">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleSelectAll}
                  data-testid="select-all-docs"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Seleziona Tutti
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleSelectRequired}
                  data-testid="select-required-docs"
                >
                  Solo Obbligatori
                </Button>
                <div className="flex-1" />
                {canAdmin && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => setShowSavePreset(!showSavePreset)}
                    className="text-blue-600"
                    data-testid="toggle-save-preset"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {showSavePreset ? 'Annulla' : 'Salva Preset'}
                  </Button>
                )}
              </div>
              
              {/* Save preset form */}
              {showSavePreset && (
                <div className="bg-blue-50 p-3 rounded-lg space-y-2 border border-blue-200">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nome preset (es. 'Assemblea Ordinaria')"
                      value={newPresetName}
                      onChange={(e) => setNewPresetName(e.target.value)}
                      className="flex-1"
                      data-testid="preset-name-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleSavePreset}
                      disabled={savingPreset || !newPresetName.trim()}
                      data-testid="save-preset-button"
                    >
                      {savingPreset ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva'}
                    </Button>
                  </div>
                  <Input
                    placeholder="Descrizione (opzionale)"
                    value={newPresetDesc}
                    onChange={(e) => setNewPresetDesc(e.target.value)}
                    className="text-sm"
                  />
                </div>
              )}
              
              {/* Document list */}
              <div className="space-y-2 flex-1 overflow-y-auto pr-2">
                {availableDocs.map((doc) => (
                  <div 
                    key={doc.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      selectedDocs.includes(doc.id) 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-gray-50 border-gray-200'
                    } ${doc.required ? 'opacity-90' : 'cursor-pointer hover:border-blue-300'}`}
                    onClick={() => toggleDocSelection(doc.id, doc.required)}
                    data-testid={`doc-item-${doc.id}`}
                  >
                    <Checkbox 
                      checked={selectedDocs.includes(doc.id)}
                      disabled={doc.required}
                      onCheckedChange={() => toggleDocSelection(doc.id, doc.required)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{doc.name}</span>
                        {doc.required && (
                          <Badge variant="secondary" className="text-xs">
                            Obbligatorio
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {doc.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Selection summary */}
              <div className="text-sm text-muted-foreground border-t pt-3">
                <span className="font-medium">{selectedDocs.length}</span> documenti selezionati 
                su <span className="font-medium">{availableDocs.length}</span> disponibili
                {selectedPreset && (
                  <span className="ml-2 text-blue-600">
                    (Preset: {selectedPreset.name})
                  </span>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsCustomDocsOpen(false)}
              data-testid="cancel-custom-docs"
            >
              Annulla
            </Button>
            {canAdmin && (
              <Button 
                variant="outline"
                onClick={() => {
                  setIsCustomDocsOpen(false);
                  setIsEmailSchedulerOpen(true);
                }}
                disabled={selectedDocs.length === 0}
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                data-testid="open-email-scheduler"
              >
                <Mail className="h-4 w-4 mr-2" />
                Invia via Email
              </Button>
            )}
            <Button 
              onClick={handleGenerateCustomDocs}
              disabled={customDocsGenerating || selectedDocs.length === 0}
              className={customDocsType === 'PREVENTIVO' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}
              data-testid="generate-custom-docs"
            >
              {customDocsGenerating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Genera PDF ({selectedDocs.length} doc.)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Email Scheduler Dialog */}
      <BudgetEmailScheduler
        isOpen={isEmailSchedulerOpen}
        onClose={() => setIsEmailSchedulerOpen(false)}
        condoId={condoId}
        yearId={selectedYear?.id}
        docType={customDocsType}
        selectedDocs={selectedDocs}
        availableDocs={availableDocs}
        token={token}
      />
    </div>
  );
};
